const DATABASE_NAME = 'lumi-private-photo-references';
const STORE_NAME = 'photo-references';

type StoredPhotoReference = {
  accountId: string;
  blob: Blob;
  updatedAt: number;
};

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Der private Foto-Speicher ist in diesem Browser nicht verfügbar.'));
      return;
    }

    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'accountId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Der private Foto-Speicher konnte nicht geöffnet werden.'));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Der private Foto-Speicher konnte nicht aktualisiert werden.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Der private Foto-Speicher konnte nicht aktualisiert werden.'));
    transaction.onabort = () => reject(transaction.error || new Error('Der private Foto-Speicher wurde abgebrochen.'));
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Das Foto konnte nicht gelesen werden.'));
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const match = /^data:([^;,]+)(?:;charset=[^;,]+)?;base64,([\s\S]+)$/i.exec(dataUrl);
  if (!match) throw new Error('Das Referenzfoto konnte nicht gespeichert werden.');
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: match[1] || 'image/jpeg' });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Das Foto konnte nicht vorbereitet werden.'));
    }, 'image/jpeg', quality);
  });
}

function loadImageSource(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Dieses Fotoformat kann auf diesem Gerät nicht gelesen werden. Bitte wähle ein JPG, PNG oder WebP-Foto.'));
    image.src = source;
  });
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  // Object URLs are fast in modern browsers. Some mobile browser/WebView
  // combinations occasionally reject them, so retry once using a data URL.
  const objectUrl = URL.createObjectURL(file);
  try {
    return await loadImageSource(objectUrl);
  } catch {
    return loadImageSource(await blobToDataUrl(file));
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Prepares a compact copy for the one story request. The original file stays
 * on the family device and is never sent to browser storage or the library.
 */
export async function optimisePhotoForStory(
  file: File,
  maxDimension = 1400,
  maxBytes = 1_100_000,
): Promise<string> {
  const image = await loadImage(file);
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = Math.min(1, maxDimension / Math.max(1, longestSide));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Das Foto konnte nicht vorbereitet werden.');
  context.drawImage(image, 0, 0, width, height);

  let output: Blob | undefined;
  for (const quality of [0.86, 0.76, 0.66]) {
    const candidate = await canvasToBlob(canvas, quality);
    output = candidate;
    if (candidate.size <= maxBytes) break;
  }
  return blobToDataUrl(output!);
}

export async function getSavedPhotoReference(accountId: string): Promise<string | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const record = await requestResult(transaction.objectStore(STORE_NAME).get(accountId) as IDBRequest<StoredPhotoReference | undefined>);
    return record?.blob ? blobToDataUrl(record.blob) : undefined;
  } finally {
    database.close();
  }
}

export async function savePhotoReference(accountId: string, dataUrl: string): Promise<void> {
  // Some mobile WebViews block fetch(dataUrl) despite supporting IndexedDB.
  // Decode locally so a private reference never leaves this device.
  const blob = dataUrlToBlob(dataUrl);
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put({ accountId, blob, updatedAt: Date.now() } satisfies StoredPhotoReference);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function removeSavedPhotoReference(accountId: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(accountId);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}
